import React, { useState, useMemo } from 'react';
import './VirtualAssistantConfig.css';

interface VirtualAssistantConfigProps {
  onClose: () => void;
  loading: boolean;
  configData: any[] | null;
}

const VirtualAssistantConfig: React.FC<VirtualAssistantConfigProps> = ({ onClose, loading, configData }) => {
  const [selectedConfig, setSelectedConfig] = useState<any>(configData && configData.length > 0 ? configData[0] : null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const filteredConfigData = useMemo(() => {
    if (!configData || !searchQuery.trim()) {
      return configData;
    }

    const query = searchQuery.toLowerCase();

    return configData.filter((config: any) => {
      const nameMatch = (config.assistant_name || config.agentName || config.name || '')
        .toLowerCase()
        .includes(query);
      const idMatch = (config.id || '').toLowerCase().includes(query);
      const agentModelMatch = (config.agent_model || '')
        .toLowerCase()
        .includes(query);
      const customerModelMatch = (config.customer_model || '')
        .toLowerCase()
        .includes(query);

      // Check skill IDs
      let skillMatch = false;
      if (config.skill_ids) {
        if (Array.isArray(config.skill_ids)) {
          skillMatch = config.skill_ids.some((id: any) =>
            String(id).toLowerCase().includes(query)
          );
        } else {
          skillMatch = String(config.skill_ids).toLowerCase().includes(query);
        }
      }

      // Check campaign IDs
      let campaignMatch = false;
      if (config.campaign_ids) {
        if (Array.isArray(config.campaign_ids)) {
          campaignMatch = config.campaign_ids.some((id: any) =>
            String(id).toLowerCase().includes(query)
          );
        } else {
          campaignMatch = String(config.campaign_ids).toLowerCase().includes(query);
        }
      }

      return nameMatch || idMatch || agentModelMatch || customerModelMatch || skillMatch || campaignMatch;
    });
  }, [configData, searchQuery]);

  return (
    <div className="va-config-overlay" onClick={onClose}>
      <div className="va-config-modal" onClick={(e) => e.stopPropagation()}>
        <div className="va-config-header">
          <h2>Virtual Assistant Configuration</h2>
          <button className="va-config-close-btn" onClick={onClose}>
            ×
          </button>
        </div>
        
        <div className="va-config-body">
          {loading ? (
            <div className="va-config-loading">
              <div className="spinner"></div>
              <p>Loading configuration...</p>
            </div>
          ) : configData && configData.length > 0 ? (
            <div className="va-config-two-column">
              <div className="va-config-list-panel">
                <h3 className="va-config-panel-title">Assistants</h3>
                <div className="va-config-search-box">
                  <input
                    type="text"
                    placeholder="Search by name, ID, model, skill, or campaign..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="va-config-search-input"
                  />
                  {searchQuery && (
                    <button
                      className="va-config-search-clear"
                      onClick={() => setSearchQuery('')}
                      title="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div className="va-config-assistant-list">
                  {filteredConfigData && filteredConfigData.length > 0 ? (
                    filteredConfigData.map((config: any, index: number) => (
                    <div
                      key={config.id || index} 
                      className={`va-config-list-item ${selectedConfig?.id === config.id ? 'selected' : ''}`}
                      onClick={() => setSelectedConfig(config)}
                    >
                      <div className="va-config-list-item-header">
                        <div className="va-config-list-item-title">
                          {config.assistant_name || config.agentName || config.name || `Configuration ${index + 1}`}
                        </div>
                        <span className={`va-config-list-badge ${!config.is_deleted ? 'active' : 'inactive'}`}>
                          {!config.is_deleted ? '●' : '○'}
                        </span>
                      </div>
                      <div className="va-config-list-item-meta">
                        {config.virtual_agent_type && (
                          <span className="meta-item">{config.virtual_agent_type}</span>
                        )}
                        {config.language_code && (
                          <span className="meta-item">{config.language_code}</span>
                        )}
                      </div>
                    </div>
                  ))
                  ) : (
                    <div className="va-config-no-results">
                      <p>No assistants found matching "{searchQuery}"</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="va-config-detail-panel">
                {selectedConfig ? (
                  <>
                    {/* Detail Header */}
                    <div className="va-config-detail-header">
                      <div className="va-config-title-section">
                        <h3>{selectedConfig.assistant_name || selectedConfig.agentName || selectedConfig.name || 'Configuration'}</h3>
                        <span className={`va-config-badge ${!selectedConfig.is_deleted ? 'active' : 'inactive'}`}>
                          {!selectedConfig.is_deleted ? 'Active' : 'Deleted'}
                        </span>
                      </div>
                      {selectedConfig.current_version && (
                        <div className="va-config-version">Version {selectedConfig.current_version}</div>
                      )}
                    </div>

                    <div className="va-config-detail-content">
                      <div className="va-config-quick-info">
                        {selectedConfig.virtual_agent_type && (
                          <div className="va-config-info-item">
                            <span className="label">Type</span>
                            <span className="value">{selectedConfig.virtual_agent_type}</span>
                          </div>
                        )}
                        {selectedConfig.language_code && (
                          <div className="va-config-info-item">
                            <span className="label">Language</span>
                            <span className="value">{selectedConfig.language_code}</span>
                          </div>
                        )}
                        {selectedConfig.current_version && (
                          <div className="va-config-info-item">
                            <span className="label">Version</span>
                            <span className="value">{selectedConfig.current_version}</span>
                          </div>
                        )}
                        {selectedConfig.status && (
                          <div className="va-config-info-item">
                            <span className="label">Status</span>
                            <span className="value">{selectedConfig.status}</span>
                          </div>
                        )}
                      </div>

                      <div className="va-config-section">
                        <h4 className="va-config-section-title">
                          Features
                        </h4>
                        <div className="va-config-features-grid">
                          {[
                            { key: 'enable_guidance', label: 'Guidance' },
                            { key: 'enable_knowledge', label: 'Knowledge' },
                            { key: 'enable_knowledge_dist', label: 'Knowledge Dist' },
                            { key: 'enable_nlp', label: 'NLP' },
                            { key: 'enable_summary', label: 'Summary' },
                            { key: 'enable_summary_v2', label: 'Summary V2' },
                            { key: 'enable_training', label: 'Training' },
                            { key: 'enable_transcription', label: 'Transcription' },
                            { key: 'swear_word_redacted', label: 'Swear Filter' },
                            { key: 'total_numeric_redacted', label: 'Numeric Filter' }
                          ].map(({ key, label }) => (
                            selectedConfig[key] !== undefined && (
                              <div key={key} className={`va-config-feature-badge ${selectedConfig[key] ? 'enabled' : 'disabled'}`}>
                                <span className="feature-label">{label}</span>
                                <span className={`feature-status ${selectedConfig[key] ? 'on' : 'off'}`}>
                                  {selectedConfig[key] ? 'ON' : 'OFF'}
                                </span>
                              </div>
                            )
                          ))}
                        </div>
                      </div>

                  <details className="va-config-expandable" open>
                    <summary className="va-config-section-title">
                      Models & Profiles
                    </summary>
                    <div className="va-config-content-grid">
                      {selectedConfig.agent_model && (
                        <div className="va-config-field">
                          <label>Agent Model</label>
                          <div className="value">{selectedConfig.agent_model}</div>
                        </div>
                      )}
                      {selectedConfig.customer_model && (
                        <div className="va-config-field">
                          <label>Customer Model</label>
                          <div className="value">{selectedConfig.customer_model}</div>
                        </div>
                      )}
                      {selectedConfig.agent_conv_profile_disp_name && (
                        <div className="va-config-field">
                          <label>Agent Profile</label>
                          <div className="value">{selectedConfig.agent_conv_profile_disp_name}</div>
                        </div>
                      )}
                      {selectedConfig.customer_conv_profile_disp_name && (
                        <div className="va-config-field">
                          <label>Customer Profile</label>
                          <div className="value">{selectedConfig.customer_conv_profile_disp_name}</div>
                        </div>
                      )}
                      {selectedConfig.conv_profile_id && (
                        <div className="va-config-field full-width">
                          <label>Conv Profile ID</label>
                          <div className="value code">{selectedConfig.conv_profile_id}</div>
                        </div>
                      )}
                      {selectedConfig.agent_conv_profile_id && (
                        <div className="va-config-field full-width">
                          <label>Agent Conv Profile ID</label>
                          <div className="value code">
                            {selectedConfig.agent_conv_profile_id === null ? 'null' : selectedConfig.agent_conv_profile_id}
                          </div>
                        </div>
                      )}
                      {selectedConfig.customer_conv_profile_id && (
                        <div className="va-config-field full-width">
                          <label>Customer Conv Profile ID</label>
                          <div className="value code">
                            {selectedConfig.customer_conv_profile_id === null ? 'null' : selectedConfig.customer_conv_profile_id}
                          </div>
                        </div>
                      )}
                    </div>
                  </details>

                  <details className="va-config-expandable">
                    <summary className="va-config-section-title">
                      Campaign & Skill Configuration
                    </summary>
                    <div className="va-config-content-grid">
                      {selectedConfig.campaign_ids && (
                        <div className="va-config-field full-width">
                          <label>Campaign IDs</label>
                          <div className="value code">
                            {selectedConfig.campaign_ids === null 
                              ? 'null' 
                              : Array.isArray(selectedConfig.campaign_ids)
                                ? selectedConfig.campaign_ids.join(', ')
                                : selectedConfig.campaign_ids}
                          </div>
                        </div>
                      )}
                      {selectedConfig.skill_ids && (
                        <div className="va-config-field full-width">
                          <label>Skill IDs</label>
                          <div className="value code">
                            {selectedConfig.skill_ids === null 
                              ? 'null' 
                              : Array.isArray(selectedConfig.skill_ids)
                                ? selectedConfig.skill_ids.join(', ')
                                : selectedConfig.skill_ids}
                          </div>
                        </div>
                      )}
                      {selectedConfig.agent_ids && (
                        <div className="va-config-field full-width">
                          <label>Agent IDs</label>
                          <div className="value code">
                            {selectedConfig.agent_ids === null 
                              ? 'null' 
                              : Array.isArray(selectedConfig.agent_ids)
                                ? selectedConfig.agent_ids.join(', ')
                                : selectedConfig.agent_ids}
                          </div>
                        </div>
                      )}
                      {selectedConfig.station_ids && (
                        <div className="va-config-field full-width">
                          <label>Station IDs</label>
                          <div className="value code">
                            {selectedConfig.station_ids === null ? 'null' : JSON.stringify(selectedConfig.station_ids)}
                          </div>
                        </div>
                      )}
                    </div>
                  </details>

                  <details className="va-config-expandable">
                    <summary className="va-config-section-title">
                      Advanced Settings
                    </summary>
                    <div className="va-config-content-grid">
                      {selectedConfig.subscription_url && (
                        <div className="va-config-field full-width">
                          <label>Subscription URL</label>
                          <div className="value code break-word">{selectedConfig.subscription_url}</div>
                        </div>
                      )}
                      {selectedConfig.summary_prompt_id_v2 && (
                        <div className="va-config-field">
                          <label>Summary Prompt ID V2</label>
                          <div className="value">{selectedConfig.summary_prompt_id_v2}</div>
                        </div>
                      )}
                      {selectedConfig.knowledge_group_id && (
                        <div className="va-config-field">
                          <label>Knowledge Group ID</label>
                          <div className="value">{selectedConfig.knowledge_group_id}</div>
                        </div>
                      )}
                      {selectedConfig.sample_rate && (
                        <div className="va-config-field">
                          <label>Sample Rate</label>
                          <div className="value">{selectedConfig.sample_rate}</div>
                        </div>
                      )}
                      {selectedConfig.suggestion_limit !== undefined && (
                        <div className="va-config-field">
                          <label>Suggestion Limit</label>
                          <div className="value">{selectedConfig.suggestion_limit}</div>
                        </div>
                      )}
                      {selectedConfig.attempts && (
                        <div className="va-config-field">
                          <label>Attempts</label>
                          <div className="value">{selectedConfig.attempts}</div>
                        </div>
                      )}
                      {selectedConfig.max_transcripts && (
                        <div className="va-config-field">
                          <label>Max Transcripts</label>
                          <div className="value">{selectedConfig.max_transcripts}</div>
                        </div>
                      )}
                    </div>
                  </details>

                  {/* Audit Information */}
                  <details className="va-config-expandable">
                    <summary className="va-config-section-title">
                      Audit Information
                    </summary>
                    <div className="va-config-content-grid">
                      {selectedConfig.last_modified_at && (
                        <div className="va-config-field">
                          <label>Last Modified</label>
                          <div className="value">
                            {typeof selectedConfig.last_modified_at === 'string' 
                              ? selectedConfig.last_modified_at 
                              : new Date(selectedConfig.last_modified_at).toLocaleString()}
                          </div>
                        </div>
                      )}
                      {selectedConfig.last_modified_by && (
                        <div className="va-config-field">
                          <label>Modified By</label>
                          <div className="value">{selectedConfig.last_modified_by}</div>
                        </div>
                      )}
                      {selectedConfig.created_at && (
                        <div className="va-config-field">
                          <label>Created At</label>
                          <div className="value">
                            {typeof selectedConfig.created_at === 'string' 
                              ? selectedConfig.created_at 
                              : new Date(selectedConfig.created_at).toLocaleString()}
                          </div>
                        </div>
                      )}
                      {selectedConfig.created_by && (
                        <div className="va-config-field">
                          <label>Created By</label>
                          <div className="value">{selectedConfig.created_by}</div>
                        </div>
                      )}
                      {selectedConfig.hash && (
                        <div className="va-config-field full-width">
                          <label>Hash</label>
                          <div className="value code break-word">{selectedConfig.hash}</div>
                        </div>
                      )}
                    </div>
                  </details>

                  <details className="va-config-expandable">
                    <summary className="va-config-section-title">
                      Other Properties
                    </summary>
                    <div className="va-config-content-grid">
                      {Object.entries(selectedConfig)
                        .filter(([key]) => ![
                          'id', 'assistant_name', 'is_deleted', 'virtual_agent_type', 'language_code', 
                          'status', 'current_version', 'agent_model', 'customer_model', 
                          'agent_conv_profile_disp_name', 'customer_conv_profile_disp_name',
                          'conv_profile_id', 'agent_conv_profile_id', 'customer_conv_profile_id',
                          'enable_guidance', 'enable_knowledge', 'enable_knowledge_dist', 'enable_nlp',
                          'enable_summary', 'enable_summary_v2', 'enable_training', 'enable_transcription',
                          'swear_word_redacted', 'total_numeric_redacted', 'campaign_ids', 'skill_ids',
                          'agent_ids', 'station_ids', 'subscription_url', 'summary_prompt_id_v2',
                          'knowledge_group_id', 'sample_rate', 'suggestion_limit', 'attempts',
                          'max_transcripts', 'last_modified_at', 'last_modified_by', 'created_at',
                          'created_by', 'hash'
                        ].includes(key))
                        .map(([key, value]) => (
                          <div key={key} className="va-config-field">
                            <label>{key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}</label>
                            <div className="value">
                              {value === null ? 'null' : (typeof value === 'object' ? JSON.stringify(value) : String(value))}
                            </div>
                          </div>
                        ))}
                    </div>
                  </details>
                    </div>
                  </>
                ) : (
                  <div className="va-config-detail-empty">
                    <p>Select an assistant to view details</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="va-config-empty">
              <h3>No Configuration Found</h3>
              <p>No virtual assistant configuration found for this domain.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VirtualAssistantConfig;
